import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  fetchBalance,
  getInitData,
  loginDev,
  loginWithInitData,
  takeTokenFromUrl,
  userIdFromToken,
  type AuthUser,
} from './api/auth'
import { CrashChart } from './components/CrashChart'
import { BetPanel } from './components/BetPanel'
import { RoundHistory } from './components/RoundHistory'
import { PlayerList } from './components/PlayerList'
import { useCrashSocket } from './ws/useCrashSocket'
import { goLobby } from './lobby'
import { translateError } from './i18n'
import { useCountUp } from './useCountUp'
import { GameHeader } from './components/GameHeader'
import { BottomSheetDrawer, type SheetTab } from './components/BottomSheetDrawer'
import brandMark from '../../shared/assets/round6-snowfold-atlas-mark-v1.png'

export default function App() {
  const { t, i18n } = useTranslation()
  useEffect(() => {
    document.title = t('title')
  }, [t, i18n.language])

  const [token, setToken] = useState<string | null>(
    () =>
      takeTokenFromUrl() ||
      (import.meta.env.VITE_DEV_TOKEN as string | undefined) ||
      null,
  )
  const [user, setUser] = useState<AuthUser | null>(null)
  const [balance, setBalance] = useState(0)
  const [authError, setAuthError] = useState<string | null>(null)
  const [authLoading, setAuthLoading] = useState(true)
  const [authRetry, setAuthRetry] = useState(0)
  const [pendingAction, setPendingAction] = useState<'bet' | 'cashout' | null>(null)
  const pendingActionRef = useRef<'bet' | 'cashout' | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [balanceWarning, setBalanceWarning] = useState<string | null>(null)
  const [balanceRefreshing, setBalanceRefreshing] = useState(false)
  const [balanceSyncedRevision, setBalanceSyncedRevision] = useState(0)
  const balanceRefreshRef = useRef(false)

  // Secondary Bottom Sheet State
  const [sheetOpen, setSheetOpen] = useState(false)
  const [sheetTab, setSheetTab] = useState('history')

  const displayBalance = useCountUp(balance)
  const betMaxRef = useRef(0)
  const latestSnapRef = useRef({ phase: 'waiting', multiplier: 1, crashPoint: null as number | null })
  const [liveGameStatus, setLiveGameStatus] = useState('')

  useEffect(() => {
    let cancelled = false
    setAuthLoading(true)
    setAuthError(null)
    void (async () => {
      try {
        if (token && !user) {
          const bal = await fetchBalance(token)
          if (!cancelled) {
            setBalance(bal)
            const uid = userIdFromToken(token)
            setUser({
              id: uid,
              telegram_id: 0,
              username: 'player',
              first_name: 'Player',
              last_name: '',
              balance: bal,
            })
            setAuthLoading(false)
          }
          return
        }
        const initData = await getInitData()
        const res = initData
          ? await loginWithInitData(initData)
          : await loginDev({ username: 'local_dev', first_name: 'Local' })
        if (cancelled) return
        setToken(res.token)
        setUser(res.user)
        setBalance(res.user.balance)
        setAuthLoading(false)
      } catch (e) {
        if (!cancelled) {
          void e
          setAuthError(t('authFailed'))
          setAuthLoading(false)
        }
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authRetry])

  const { snap, chartPoints, placeBet, cashout } = useCrashSocket(
    token,
    user?.id ?? null,
  )
  const balanceNeedsSync = snap.balanceSyncRevision > balanceSyncedRevision
  latestSnapRef.current = {
    phase: snap.phase,
    multiplier: snap.multiplier,
    crashPoint: snap.crashPoint,
  }

  useEffect(() => {
    const update = () => {
      const current = latestSnapRef.current
      if (current.phase === 'running') {
        setLiveGameStatus(t('chart.liveMultiplier', { mult: current.multiplier.toFixed(2) }))
      } else if (current.phase === 'crashed' && current.crashPoint != null) {
        setLiveGameStatus(t('chart.crashAt', { mult: current.crashPoint.toFixed(2) }))
      } else {
        setLiveGameStatus(t(`phase.${current.phase}`))
      }
    }
    update()
    if (snap.phase !== 'running') return
    const id = window.setInterval(update, 1000)
    return () => window.clearInterval(id)
  }, [snap.phase, snap.roundId, t])

  const refreshBalance = useCallback(async (syncRevision?: number) => {
    if (!token || balanceRefreshRef.current) return false
    balanceRefreshRef.current = true
    setBalanceRefreshing(true)
    try {
      const bal = await fetchBalance(token)
      setBalance(bal)
      if (syncRevision != null) {
        setBalanceSyncedRevision((current) => Math.max(current, syncRevision))
      }
      setBalanceWarning(null)
      return true
    } catch {
      setBalanceWarning(t('balanceRefreshFailed'))
      return false
    } finally {
      balanceRefreshRef.current = false
      setBalanceRefreshing(false)
    }
  }, [t, token])

  useEffect(() => {
    if (!balanceNeedsSync || balanceWarning || balanceRefreshing) return
    void refreshBalance(snap.balanceSyncRevision)
  }, [
    balanceNeedsSync,
    balanceRefreshing,
    balanceWarning,
    refreshBalance,
    snap.balanceSyncRevision,
  ])

  useEffect(() => {
    if (!pendingAction) return
    const acknowledged =
      pendingAction === 'bet'
        ? Boolean(snap.myBet) || snap.phase !== 'betting'
        : Boolean(snap.myBet?.cashed) || snap.phase !== 'running'
    if (acknowledged || snap.lastError || snap.settlementIssue || !snap.connected) {
      pendingActionRef.current = null
      setPendingAction(null)
    }
  }, [
    pendingAction,
    snap.connected,
    snap.lastError,
    snap.myBet,
    snap.phase,
    snap.settlementIssue,
  ])

  if (authLoading) {
    return (
      <div className="center" role="status" aria-live="polite">
        <div className="spinner" aria-hidden />
        <p>{t('connecting')}</p>
      </div>
    )
  }

  if (authError || !token) {
    return (
      <div className="center" role="alert">
        <p className="error">{authError ?? t('unauthorized')}</p>
        <p className="hint">{t('openFromLobby')}</p>
        <button type="button" className="btn-lobby" onClick={() => setAuthRetry((n) => n + 1)}>
          {t('retryLogin')}
        </button>
        <button type="button" className="btn-lobby" onClick={() => goLobby(token)}>
          {t('backLobby')}
        </button>
      </div>
    )
  }

  const crashed = snap.phase === 'crashed'
  const settlementNotice = snap.settlementIssue
    ? snap.settlementIssue.roundId
      ? t('settlementReviewDetail', {
          round: snap.settlementIssue.roundId.slice(0, 8),
        })
      : t('settlementReviewDetailUnknown')
    : null
  const displayError = settlementNotice ?? (snap.lastError
    ? translateError(snap.lastError, t)
    : actionError ?? balanceWarning)

  // Betting countdown progress
  if (snap.phase === 'betting' && snap.bettingEndsIn != null) {
    betMaxRef.current = Math.max(betMaxRef.current, snap.bettingEndsIn)
  } else if (snap.phase !== 'betting') {
    betMaxRef.current = 0
  }
  const betPct =
    snap.phase === 'betting' && snap.bettingEndsIn != null && betMaxRef.current > 0
      ? Math.max(0, Math.min(1, snap.bettingEndsIn / betMaxRef.current))
      : 0

  const drawerTabs: SheetTab[] = [
    {
      id: 'history',
      label: t('history'),
      content: <RoundHistory history={snap.history} token={token} />,
    },
    {
      id: 'players',
      label: t('players'),
      content: (
        <section className="section live-ledger" style={{ padding: 0, margin: 0, border: 'none', background: 'transparent' }}>
          {snap.presence ? (
            <p className="presence" style={{ marginBottom: 12 }}>
              {t('presence', {
                seated: snap.presence.realPlayers + snap.presence.bots,
                watching: snap.presence.watching,
                pot: snap.presence.totalBet,
              })}
            </p>
          ) : null}
          <PlayerList players={snap.players} selfId={user?.id ?? null} />
        </section>
      ),
    },
    {
      id: 'rules',
      label: t('rules'),
      content: (
        <div style={{ lineHeight: 1.6 }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '1rem', color: '#fff' }}>{t('rulesTitle')}</h3>
          <p style={{ color: 'var(--text-secondary)' }}>{t('rulesBody')}</p>
        </div>
      ),
    },
  ]

  return (
    <div className="app app-single-viewport" data-game="crash">
      <GameHeader
        title={t('title')}
        brandSrc={brandMark}
        lobbyLabel={t('backLobby')}
        onLobby={() => goLobby(token)}
        balance={displayBalance}
        balanceLabel={t('balance', { amount: Math.round(displayBalance) })}
        connected={snap.connected}
        connectedLabel={t('connected')}
        disconnectedLabel={t('reconnecting')}
        meta={snap.roundId ? snap.roundId.slice(0, 8) : undefined}
        onOpenPlayers={() => {
          setSheetTab('players')
          setSheetOpen(true)
        }}
        playersLabel={t('players')}
        onOpenHistory={() => {
          setSheetTab('history')
          setSheetOpen(true)
        }}
        historyLabel={t('history')}
        onOpenRules={() => {
          setSheetTab('rules')
          setSheetOpen(true)
        }}
        rulesLabel={t('rules')}
      />

      <RoundHistory history={snap.history} token={token} />

      {snap.phase === 'betting' && snap.bettingEndsIn != null && (
        <div
          className={`timer-track${betPct <= 0.25 ? ' is-urgent' : ''}`}
          role="timer"
          aria-label={t('bettingLeft', { sec: snap.bettingEndsIn.toFixed(1) })}
        >
          <div
            className="timer-fill"
            style={{ transform: `scaleX(${betPct})` }}
          />
        </div>
      )}

      <div className="game-stage-viewport">
        <div className={`chart-block phase-${snap.phase}`} style={{ width: '100%', height: '100%', position: 'relative' }}>
          <CrashChart
            points={chartPoints}
            multiplier={snap.multiplier}
            crashed={crashed}
            crashPoint={snap.crashPoint}
            phase={snap.phase}
            roundId={snap.roundId}
            labels={{
              waiting: t('chart.waiting'),
              flying: t('chart.flying'),
              crashAt: (m) => t('chart.crashAt', { mult: m.toFixed(2) }),
              aria: t('chart.aria'),
            }}
          />
          <span
            role="status"
            aria-live="polite"
            aria-atomic="true"
            style={{
              position: 'absolute',
              width: 1,
              height: 1,
              padding: 0,
              margin: -1,
              overflow: 'hidden',
              clip: 'rect(0, 0, 0, 0)',
              whiteSpace: 'nowrap',
              border: 0,
            }}
          >
            {liveGameStatus}
          </span>
          {snap.myBet?.cashed && (
            <div key={`cash-${snap.roundId}`} className="cash-flash" aria-hidden />
          )}
        </div>
      </div>

      <BetPanel
        phase={snap.phase}
        balance={balance}
        hasBet={!!snap.myBet}
        cashed={!!snap.myBet?.cashed}
        confirmedAutoCashoutAt={snap.myBet?.autoCashoutAt}
        connected={snap.connected}
        pendingAction={pendingAction}
        balanceOutOfSync={Boolean(balanceWarning) || balanceNeedsSync}
        balanceRefreshing={balanceRefreshing}
        settlementBlocked={snap.settlementIssue != null}
        onRefreshBalance={() => void refreshBalance(snap.balanceSyncRevision)}
        onBet={(amt, autoAt) => {
          if (pendingActionRef.current || snap.settlementIssue) return
          setActionError(null)
          const sent = placeBet(amt, autoAt)
          if (!sent) {
            setActionError(t('connectionUnavailable'))
            return
          }
          pendingActionRef.current = 'bet'
          setPendingAction('bet')
        }}
        onCashout={() => {
          if (pendingActionRef.current || snap.settlementIssue) return
          setActionError(null)
          const sent = cashout()
          if (!sent) {
            setActionError(t('connectionUnavailable'))
            return
          }
          pendingActionRef.current = 'cashout'
          setPendingAction('cashout')
        }}
        error={displayError}
        bettingEndsIn={snap.bettingEndsIn}
      />

      <BottomSheetDrawer
        isOpen={sheetOpen}
        onClose={() => setSheetOpen(false)}
        tabs={drawerTabs}
        initialTabId={sheetTab}
        closeLabel={t('close')}
      />
    </div>
  )
}
